// @ts-nocheck — customer_feedback + customer_product_requests not in types
//
// Auto-popup that appears on every dashboard load when there is unread
// customer feedback (status='new') OR pending product requests (status='pending').
// Dismissed via the X button OR by clicking "View" which navigates to the
// relevant page. The session-storage gate prevents the popup from re-firing
// while the user is mid-browse — it only shows ONCE per browser tab session
// unless the unread counts change.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Bell, MessageSquare, PackagePlus, X, ArrowLeft } from 'lucide-react'
import { supabase as typedSupabase } from '@/lib/supabase'

const supabase = typedSupabase as any

function useInboxCounts() {
  return useQuery({
    queryKey: ['dashboard-inbox-counts'],
    refetchOnWindowFocus: false,
    refetchInterval: 60_000, // refresh every minute so badges stay live
    refetchIntervalInBackground: false,
    queryFn: async (): Promise<{ feedback: number; requests: number }> => {
      const [fb, req] = await Promise.all([
        supabase
          .from('customer_feedback')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'new'),
        supabase
          .from('customer_product_requests')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'pending'),
      ])
      return {
        feedback: fb.count ?? 0,
        requests: req.count ?? 0,
      }
    },
  })
}

export default function CustomerInboxPopup() {
  const { data, isLoading } = useInboxCounts()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const total = (data?.feedback ?? 0) + (data?.requests ?? 0)

  // Behavior the owner asked for:
  //   • Popup shows on every dashboard load when there is unread feedback or
  //     pending product requests.
  //   • Dismissed (X or "View") for the rest of this dashboard session — the
  //     `dismissed` local state hides it without writing to storage.
  //   • Full page reload (F5) re-mounts this component → state resets → popup
  //     re-shows if items remain.
  //   • If new items arrive mid-session (refetchInterval=60s), the popup pops
  //     again only if the user hadn't already dismissed it.
  const [dismissed, setDismissed] = useState(false)
  useEffect(() => {
    if (isLoading || total === 0 || dismissed) return
    setOpen(true)
  }, [total, isLoading, dismissed])

  const dismiss = () => {
    setDismissed(true)
    setOpen(false)
  }

  const goTo = (path: string) => {
    setDismissed(true)
    setOpen(false)
    navigate(path)
  }

  if (!open || total === 0) return null

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-end sm:items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={dismiss}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in slide-in-from-bottom duration-300"
        dir="rtl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-emerald-500 to-emerald-600 px-5 pt-5 pb-6">
          <button
            onClick={dismiss}
            className="absolute top-3 left-3 text-white/80 hover:text-white p-1"
            aria-label="إغلاق"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-white text-lg font-bold">لديك رسائل جديدة من العملاء</h2>
              <p className="text-white/80 text-xs">{total} عنصر بانتظار المراجعة</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-3 space-y-2">
          {data!.feedback > 0 && (
            <button
              onClick={() => goTo('/customer-feedback')}
              className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 transition group"
            >
              <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <MessageSquare className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 text-right">
                <div className="text-sm font-bold text-slate-900">
                  {data!.feedback} ملاحظة جديدة
                </div>
                <div className="text-xs text-slate-500">
                  من العملاء عبر التطبيق والموقع
                </div>
              </div>
              <ArrowLeft className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 group-hover:-translate-x-0.5 transition-all" />
            </button>
          )}

          {data!.requests > 0 && (
            <button
              onClick={() => goTo('/product-requests')}
              className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 transition group"
            >
              <div className="w-11 h-11 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                <PackagePlus className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1 text-right">
                <div className="text-sm font-bold text-slate-900">
                  {data!.requests} طلب منتج جديد
                </div>
                <div className="text-xs text-slate-500">
                  بانتظار التوفير أو اقتراح بديل
                </div>
              </div>
              <ArrowLeft className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 group-hover:-translate-x-0.5 transition-all" />
            </button>
          )}
        </div>

        <div className="px-5 pb-4 pt-1">
          <button
            onClick={dismiss}
            className="w-full py-2.5 text-sm text-slate-500 hover:text-slate-700 rounded-lg hover:bg-slate-50"
          >
            عرض لاحقاً
          </button>
        </div>
      </div>
    </div>
  )
}
